import type { ComplexStyleRule } from "@vanilla-extract/css";
import { isStringNumber } from "@huub-web/utils";

import { breakpoints } from "../theme/breakpoints";
import type { BreakpointName } from "../theme/types/BreakpointName";

export type ResponsiveStyleRules = Partial<
  Record<BreakpointName | number, ComplexStyleRule>
>;

/**
 * Applies responsive styles based on the viewport width.
 */
export function responsive<T extends object = ComplexStyleRule>(
  responsiveDefinition: ResponsiveStyleRules,
  config: {
    query?: "media" | "container";
    value?: "layoutContent" | "container" | "card" | string;
  } = {
    query: "media",
  },
): T {
  const breakpointsWithBase = { base: 0, ...breakpoints };

  // Convert entries to an array with numeric breakpoint values
  const entries = Object.entries(responsiveDefinition)
    .map(([breakpoint, rule]) => {
      let numericValue: number;

      if (breakpointsWithBase[breakpoint as BreakpointName] !== undefined) {
        numericValue = breakpointsWithBase[breakpoint as BreakpointName];
      } else if (isStringNumber(breakpoint)) {
        numericValue = parseInt(breakpoint, 10);
      } else {
        return null;
      }

      return { breakpoint, numericValue, rule };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);

  // Sort by numeric breakpoint value (ascending)
  entries.sort((a, b) => a.numericValue - b.numericValue);

  const media = {
    [config?.query === "container" ? "@container" : "@media"]:
      Object.fromEntries(
        entries.map(({ numericValue, rule }) => [
          `${config?.query === "container" ? (config.value ?? "container") : "screen and"} (min-width: ${numericValue}px)`,
          rule,
        ]),
      ),
  } as T;

  return media;
}

/**
 * Applies responsive styles based on the parent Container width.
 */
export function responsiveContainer<T extends object = ComplexStyleRule>(
  responsiveDefinition: ResponsiveStyleRules,
): T {
  return responsive(responsiveDefinition, {
    query: "container",
    value: "container",
  });
}

/**
 * Applies responsive styles based on the parent Container width.
 */
export function responsiveCard<T extends object = ComplexStyleRule>(
  responsiveDefinition: ResponsiveStyleRules,
): T {
  return responsive(responsiveDefinition, {
    query: "container",
    value: "card",
  });
}
